/**
 * TaskHierarchy — Efficient O(1) hierarchy lookups for flat WBS-position arrays.
 *
 * Replaces all O(n) `tasks.filter(t => t.position.startsWith(...))` calls
 * with Map-based lookups.
 *
 * Usage:
 *   const h = buildHierarchy(tasks);
 *   h.getChildren("1.400")        // direct children of position "1.400"
 *   h.getAllDescendants("1.400")   // all nested descendants
 *   h.getParent("1.400.0.3")      // task at position "1.400.0"
 *   h.getAncestors("1.400.0.3")   // [task@"1.400.0", task@"1.400", task@"1"]
 *   h.getByPosition("1.400")      // task at that position
 *   h.findAncestor("1.400.0.3", t => t.record === 'T')  // first ancestor matching predicate
 */

/**
 * Derives the parent position by removing the last dot-segment.
 * "1.400.0.3" → "1.400.0"
 * "1" → null (root)
 */
export function getParentPosition(position) {
  const lastDot = position.lastIndexOf('.');
  return lastDot === -1 ? null : position.substring(0, lastDot);
}

/**
 * Builds lookup maps from a flat task array with `position` fields.
 * Time: O(n). Space: O(n).
 *
 * @param {Array} tasks - flat array where each task has a `position` string
 * @returns {Object} hierarchy API
 */
export function buildHierarchy(tasks) {
  if (!tasks || tasks.length === 0) {
    return {
      positionMap: new Map(),
      childrenMap: new Map(),
      getChildren: () => [],
      getAllDescendants: () => [],
      getByPosition: () => undefined,
      getParent: () => undefined,
      getAncestors: () => [],
      findAncestor: () => undefined,
    };
  }

  // Map<position, task>
  const positionMap = new Map();
  // Map<parentPosition, task[]>
  const childrenMap = new Map();

  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    positionMap.set(task.position, task);

    const parentPos = getParentPosition(task.position);
    if (parentPos !== null) {
      let siblings = childrenMap.get(parentPos);
      if (!siblings) {
        siblings = [];
        childrenMap.set(parentPos, siblings);
      }
      siblings.push(task);
    }
  }

  /** Direct children of a position (O(1)) */
  const getChildren = (position) => childrenMap.get(position) || [];

  /** All descendants via BFS on childrenMap (O(subtree size)) */
  const getAllDescendants = (position) => {
    const result = [];
    const queue = childrenMap.get(position);
    if (!queue) return result;
    const stack = [...queue];
    while (stack.length > 0) {
      const node = stack.pop();
      result.push(node);
      const nodeChildren = childrenMap.get(node.position);
      if (nodeChildren) {
        for (let i = 0; i < nodeChildren.length; i++) {
          stack.push(nodeChildren[i]);
        }
      }
    }
    return result;
  };

  /** Task at a given position (O(1)) */
  const getByPosition = (position) => positionMap.get(position);

  /** Direct parent task (O(1)) */
  const getParent = (position) => {
    const parentPos = getParentPosition(position);
    return parentPos !== null ? positionMap.get(parentPos) : undefined;
  };

  /** All ancestors from immediate parent to root (O(depth ≤ 6)) */
  const getAncestors = (position) => {
    const result = [];
    let pos = getParentPosition(position);
    while (pos !== null) {
      const ancestor = positionMap.get(pos);
      if (ancestor) result.push(ancestor);
      pos = getParentPosition(pos);
    }
    return result;
  };

  /** First ancestor matching a predicate (O(depth)) */
  const findAncestor = (position, predicate) => {
    let pos = getParentPosition(position);
    while (pos !== null) {
      const ancestor = positionMap.get(pos);
      if (ancestor && predicate(ancestor)) return ancestor;
      pos = getParentPosition(pos);
    }
    return undefined;
  };

  return {
    positionMap,
    childrenMap,
    getChildren,
    getAllDescendants,
    getByPosition,
    getParent,
    getAncestors,
    findAncestor,
  };
}
